import {
	useGetShippingCarriersQuery,
	useUpdateVetterJobShipmentTrackingMutation,
	VetterJobActionKey,
} from '@codezo/graphql';
import { SectionHead } from '@codezo/quote-detail';
import { ExternalLink, Loader2, Truck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { DatePicker } from '../../components/ui/date-picker';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectPopover } from '../../components/ui/select-popover';
import { formatDate } from '../../lib/utils';
import { JobNotice } from './JobShell';
import type { VetterJob } from './prices/price-form';
import { SHIPMENT_STATUS_LABEL, SHIPPING_METHOD_LABEL } from './status-labels';

type Shipment = VetterJob['shipments'][number];
type Carrier = { code: string; name: string };

/** Picker value for a carrier that isn't in the list — typed by hand instead. */
const OTHER_CARRIER = '__other__';

const FIELD_LABEL: Record<string, string> = {
	carrier: 'Carrier',
	trackingNumber: 'Tracking number',
	shippedAt: 'Ship date',
	estimatedDeliveryDate: 'Expected delivery',
};

/**
 * Section 3 — shipments carrying only this supplier's goods. The supplier adds
 * tracking (SHIP) or corrects it (EDIT_TRACKING); both go through one mutation
 * and the backend decides which applies. Marking delivered stays with the studio.
 */
export function ShippingSection({
	shipments,
	onChanged,
}: {
	shipments: Shipment[];
	onChanged: () => Promise<unknown>;
}) {
	const { data } = useGetShippingCarriersQuery({ fetchPolicy: 'no-cache' });
	const carriers: Carrier[] = data?.getShippingCarriers ?? [];

	return (
		<section className="flex flex-col gap-4">
			<SectionHead
				title="Shipping"
				meta="add the carrier and tracking number when a shipment leaves you"
			/>
			<div className="flex flex-col divide-y divide-line-1 rounded-[var(--r-3)] border border-line-1 bg-surface">
				{shipments.map((shipment) => (
					<ShipmentRow
						key={shipment.id}
						shipment={shipment}
						carriers={carriers}
						onChanged={onChanged}
					/>
				))}
			</div>
		</section>
	);
}

function ShipmentRow({
	shipment,
	carriers,
	onChanged,
}: {
	shipment: Shipment;
	carriers: Carrier[];
	onChanged: () => Promise<unknown>;
}) {
	const [editing, setEditing] = useState(false);
	const action = shipment.availableActions.find(
		(a) => a.key === VetterJobActionKey.Ship || a.key === VetterJobActionKey.EditTracking
	);
	const carrierName =
		carriers.find((c) => c.code === shipment.carrier)?.name ?? shipment.carrier ?? null;

	return (
		<div className="flex flex-col gap-4 px-5 py-4">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="min-w-0">
					<div className="flex items-center gap-2 text-base font-medium text-text-1">
						<Truck size={15} className="text-text-3" />
						Shipment {shipment.sequence} · Order {shipment.orderNumber}
					</div>
					<ul className="mt-1.5 flex flex-col gap-0.5 text-sm text-text-3">
						{shipment.lines.map((line) => (
							<li key={line.orderItemId}>
								{line.quantity.toLocaleString('en-US')} ×{' '}
								{line.productName ?? 'Unnamed product'}
							</li>
						))}
					</ul>
					<div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-text-4">
						<Badge variant="outline">{SHIPMENT_STATUS_LABEL[shipment.status]}</Badge>
						{shipment.shippingMethod && (
							<span>{SHIPPING_METHOD_LABEL[shipment.shippingMethod]}</span>
						)}
						{shipment.shippedAt && (
							<span>Shipped {formatDate(shipment.shippedAt)}</span>
						)}
						{shipment.estimatedDeliveryDate && (
							<span>Expected {formatDate(shipment.estimatedDeliveryDate)}</span>
						)}
					</div>
					{shipment.trackingNumber && (
						<div className="mt-1.5 text-sm text-text-2">
							{carrierName && <span>{carrierName} · </span>}
							{shipment.trackingUrl ? (
								<a
									href={shipment.trackingUrl}
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 font-mono underline-offset-4 hover:underline"
								>
									{shipment.trackingNumber}
									<ExternalLink size={11} />
								</a>
							) : (
								<span className="font-mono">{shipment.trackingNumber}</span>
							)}
						</div>
					)}
				</div>

				{action && !editing && (
					<div className="flex flex-col items-end gap-1.5">
						<Button
							variant={action.key === VetterJobActionKey.Ship ? 'default' : 'outline'}
							onClick={() => setEditing(true)}
							disabled={!!action.blockedReason}
						>
							{action.label}
						</Button>
						{action.blockedReason && (
							<span className="max-w-64 text-right text-sm text-text-4">
								{action.blockedReason}
							</span>
						)}
					</div>
				)}
			</div>

			{action && editing && (
				<TrackingForm
					shipment={shipment}
					carriers={carriers}
					requiredFields={action.requiredFields}
					submitLabel={action.label}
					onCancel={() => setEditing(false)}
					onSaved={async () => {
						setEditing(false);
						await onChanged();
					}}
				/>
			)}
		</div>
	);
}

function TrackingForm({
	shipment,
	carriers,
	requiredFields,
	submitLabel,
	onCancel,
	onSaved,
}: {
	shipment: Shipment;
	carriers: Carrier[];
	requiredFields: string[];
	submitLabel: string;
	onCancel: () => void;
	onSaved: () => Promise<void>;
}) {
	const known = !shipment.carrier || carriers.some((c) => c.code === shipment.carrier);
	const [carrierChoice, setCarrierChoice] = useState(
		known ? (shipment.carrier ?? '') : OTHER_CARRIER
	);
	const [otherCarrier, setOtherCarrier] = useState(known ? '' : (shipment.carrier ?? ''));
	const [trackingNumber, setTrackingNumber] = useState(shipment.trackingNumber ?? '');
	const [shippedAt, setShippedAt] = useState<Date | undefined>(
		shipment.shippedAt ? new Date(shipment.shippedAt) : undefined
	);
	const [eta, setEta] = useState<Date | undefined>(
		shipment.estimatedDeliveryDate ? new Date(shipment.estimatedDeliveryDate) : undefined
	);
	const [error, setError] = useState<string | null>(null);
	const [update, { loading }] = useUpdateVetterJobShipmentTrackingMutation();

	const carrier = (carrierChoice === OTHER_CARRIER ? otherCarrier : carrierChoice).trim();
	const values: Record<string, string | undefined> = {
		carrier: carrier || undefined,
		trackingNumber: trackingNumber.trim() || undefined,
		shippedAt: shippedAt?.toISOString(),
		estimatedDeliveryDate: eta?.toISOString(),
	};
	const missing = requiredFields.filter((f) => !values[f]);

	const handleSave = async () => {
		if (missing.length > 0) {
			const names = missing.map((f) => FIELD_LABEL[f] ?? f);
			setError(`${names.join(' and ')} ${names.length > 1 ? 'are' : 'is'} required.`);
			return;
		}
		setError(null);
		try {
			const { data } = await update({
				variables: {
					id: shipment.id,
					input: {
						carrier: values.carrier ?? null,
						trackingNumber: values.trackingNumber ?? null,
						shippedAt: values.shippedAt ?? null,
						estimatedDeliveryDate: values.estimatedDeliveryDate ?? null,
					},
				},
			});
			const res = data?.updateVetterJobShipmentTracking;
			if (!res?.success) {
				setError(res?.message || 'Tracking could not be saved.');
				return;
			}
			toast.success('Tracking saved');
			await onSaved();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Tracking could not be saved.');
		}
	};

	const carrierOptions = [
		...carriers.map((c) => ({ value: c.code, label: c.name })),
		{ value: OTHER_CARRIER, label: 'Other carrier…' },
	];

	return (
		<div className="flex flex-col gap-4 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken/40 p-4">
			<div className="grid gap-4 sm:grid-cols-2">
				<div className="flex flex-col gap-1.5">
					<Label>Carrier</Label>
					<SelectPopover
						value={carrierChoice}
						onChange={setCarrierChoice}
						options={carrierOptions}
						placeholder="Choose carrier…"
					/>
					{carrierChoice === OTHER_CARRIER && (
						<Input
							value={otherCarrier}
							onChange={(e) => setOtherCarrier(e.target.value)}
							placeholder="Carrier name"
						/>
					)}
				</div>
				<div className="flex flex-col gap-1.5">
					<Label>Tracking number</Label>
					<Input
						value={trackingNumber}
						onChange={(e) => setTrackingNumber(e.target.value)}
						placeholder="e.g. 1Z999AA10123456784"
					/>
				</div>
				<div className="flex flex-col gap-1.5">
					<Label>Ship date (optional)</Label>
					<DatePicker
						value={shippedAt}
						onChange={setShippedAt}
						placeholder="Today"
						clearable
					/>
				</div>
				<div className="flex flex-col gap-1.5">
					<Label>Expected delivery (optional)</Label>
					<DatePicker value={eta} onChange={setEta} placeholder="Not known" clearable />
				</div>
			</div>

			{error && <JobNotice title={error} />}

			<div className="flex justify-end gap-2">
				<Button variant="ghost" onClick={onCancel} disabled={loading}>
					Cancel
				</Button>
				<Button onClick={handleSave} disabled={loading}>
					{loading && (
						<Loader2 size={14} data-icon="inline-start" className="animate-spin" />
					)}
					{submitLabel}
				</Button>
			</div>
		</div>
	);
}
